import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { useList, errMsg } from "@/lib/api";
import { PageHeader, StatCard, StatusBadge, Spinner, EmptyState, Money } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { FormDialog } from "@/components/ResourcePage";
import { toast } from "sonner";
import {
  Rocket, Globe, Star, Megaphone, BookOpen, MessagesSquare, AlertTriangle,
  Pencil, UserCheck, StickyNote, Check, X, EyeOff, HelpCircle, ClipboardCheck, Receipt,
} from "lucide-react";

const SOURCE_CHANNELS = ["website", "facebook", "whatsapp", "google", "referral", "walk_in", "other"];
const fmtDate = (s) => (s || "").slice(0, 10) || "—";

// ---------------------------------------------------------------- /admin/growth
export function GrowthDashboard() {
  const { data: s, loading } = useList("/admin/growth/summary");
  if (loading) return <Spinner full />;
  if (!s) return <EmptyState text="Could not load growth summary." />;
  const cards = [
    { icon: Globe, label: "Public profiles", value: `${s.teacher_profiles_public}/${s.teacher_profiles_total}`, sub: "visible / total", to: "/admin/growth/teacher-profiles", id: "profiles" },
    { icon: Star, label: "Pending testimonials", value: s.pending_testimonials, sub: "awaiting moderation", to: "/admin/growth/testimonials", id: "testimonials", accent: s.pending_testimonials > 0 },
    { icon: Megaphone, label: "New leads", value: s.new_enquiries, sub: "need follow-up", to: "/admin/growth/enquiries", id: "leads", accent: s.new_enquiries > 0 },
    { icon: ClipboardCheck, label: "Pending enrolments", value: s.pending_enrolments ?? 0, sub: "awaiting review", to: "/admin/growth/enrolments", id: "enrolments", accent: (s.pending_enrolments ?? 0) > 0 },
    { icon: AlertTriangle, label: "Open escalations", value: s.open_escalations, sub: "AI needs human reply", to: "/admin/growth/ai-escalations", id: "escalations" },
    { icon: BookOpen, label: "Active knowledge", value: s.active_knowledge, sub: "articles for AI answers", to: "/admin/growth/ai-knowledge", id: "knowledge" },
    { icon: HelpCircle, label: "Active FAQs", value: s.active_faqs, sub: "public questions", to: "/admin/growth/ai-faqs", id: "faqs" },
    { icon: MessagesSquare, label: "AI conversations", value: s.ai_conversations, sub: "total chats", to: "/admin/growth/ai-conversations", id: "conversations" },
  ];
  const channels = Object.entries(s.leads_by_channel || {}).sort((a, b) => b[1] - a[1]);
  return (
    <div data-testid="growth-dashboard">
      <PageHeader title="Growth" description="Public profiles, testimonials, leads and AI customer service — everything that powers public enrolment." />
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 stagger-in">
        {cards.map((c) => (
          <Link key={c.id} to={c.to} data-testid={`growth-card-${c.id}`}>
            <StatCard icon={c.icon} label={c.label} value={c.value} sub={c.sub} accent={c.accent} />
          </Link>
        ))}
        <div className="rounded-lg border bg-card p-5 lift" data-testid="growth-card-channels">
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground mb-3">Leads by channel</p>
          {channels.length === 0 ? <p className="text-sm text-muted-foreground">No leads yet.</p> : channels.map(([ch, n]) => (
            <div key={ch} className="flex items-center justify-between text-sm py-0.5">
              <span className="capitalize">{ch.replace(/_/g, " ")}</span>
              <span className="font-bold font-display">{n}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- /admin/growth/teacher-profiles
export function GrowthTeacherProfiles() {
  const { data: rows, loading } = useList("/admin/growth/teacher-profiles");
  const navigate = useNavigate();
  if (loading) return <Spinner full />;
  return (
    <div data-testid="growth-profiles-page">
      <PageHeader title="Public teacher profiles"
        description="Control what each teacher shows on the public website. Payroll, KPI and finance data never appear here." />
      {!rows?.length ? <EmptyState text="No active teachers." /> : (
        <div className="space-y-3 stagger-in">
          {rows.map((r) => (
            <div key={r.teacher_id} className="rounded-lg border bg-card p-4 flex flex-wrap items-center gap-4 lift" data-testid={`profile-row-${r.teacher_id}`}>
              <div className="flex-1 min-w-[160px]">
                <p className="font-semibold text-sm">{r.full_name}</p>
                <p className="text-xs text-muted-foreground">{r.subjects.join(", ") || "No subjects yet"}</p>
              </div>
              <StatusBadge status={r.public_visible ? "public" : "hidden"} testId={`profile-visible-${r.teacher_id}`} />
              <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${r.booking_enabled ? "bg-[#D8F3DC] text-[#2D6A4F]" : "bg-muted text-muted-foreground"}`}>
                Booking {r.booking_enabled ? "enabled" : "disabled"}
              </span>
              <div className="text-right hidden sm:block">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Testimonials</p>
                <p className="font-bold font-display">{r.approved_testimonials_count}/{r.testimonials_count}</p>
              </div>
              <div className="w-32 hidden md:block">
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Complete {r.completeness}%</p>
                <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                  <div className="h-full rounded-full bg-secondary" style={{ width: `${r.completeness}%` }} />
                </div>
              </div>
              <span className="text-xs text-muted-foreground hidden lg:block">Updated {fmtDate(r.updated_at)}</span>
              <Button size="sm" variant="outline" onClick={() => navigate(`/admin/growth/teacher-profiles/${r.teacher_id}`)}
                data-testid={`edit-profile-${r.teacher_id}`}>
                <Pencil className="h-3.5 w-3.5 mr-1" />Edit
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- /admin/growth/teacher-profiles/:teacherId
const PROFILE_SWITCHES = [
  ["public_visible", "Public profile visible", "Show this teacher on the public website"],
  ["booking_enabled", "Online booking enabled", "Allow parents to pick this teacher when registering"],
  ["show_availability", "Show availability", "Display free time slots publicly"],
  ["show_testimonials", "Show testimonials", "Display approved testimonials"],
  ["show_exam_highlights", "Show exam highlights", "Display exam achievements"],
];

export function GrowthTeacherProfileEditor() {
  const { teacherId } = useParams();
  const { data, loading, reload } = useList(`/admin/growth/teacher-profiles/${teacherId}`);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  if (loading) return <Spinner full />;
  if (!data) return <EmptyState text="Teacher not found." />;
  const p = data.profile || {};
  const cur = draft || {
    public_visible: !!p.public_visible, booking_enabled: !!p.booking_enabled,
    show_availability: p.show_availability !== false, show_testimonials: p.show_testimonials !== false,
    show_exam_highlights: p.show_exam_highlights !== false,
    profile_photo_url: p.profile_photo_url || "", intro_video_url: p.intro_video_url || "",
    short_bio: p.short_bio || "", long_bio: p.long_bio || "",
    years_experience: p.years_experience ?? "",
    qualifications: (p.qualifications || []).join("\n"),
    achievements: (p.achievements || []).join("\n"),
  };
  const set = (k, v) => setDraft({ ...cur, [k]: v });
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/growth/teacher-profiles/${teacherId}`, { values: {
        ...Object.fromEntries(PROFILE_SWITCHES.map(([k]) => [k, !!cur[k]])),
        profile_photo_url: cur.profile_photo_url || null,
        intro_video_url: cur.intro_video_url || null,
        short_bio: cur.short_bio, long_bio: cur.long_bio,
        years_experience: cur.years_experience === "" ? null : Number(cur.years_experience),
        qualifications: cur.qualifications.split("\n").map((x) => x.trim()).filter(Boolean),
        achievements: cur.achievements.split("\n").map((x) => x.trim()).filter(Boolean),
      }});
      toast.success("Public profile saved");
      setDraft(null); reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  return (
    <div data-testid="growth-profile-editor">
      <PageHeader title={`Public profile — ${data.teacher.full_name}`}
        description={p.slug ? `Public URL slug: /teachers/${p.slug} · ${data.completeness}% complete` : "No profile yet — saving will create one."}
        actions={<Button onClick={save} disabled={saving} data-testid="save-public-profile">{saving ? "Saving..." : "Save profile"}</Button>} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-5xl">
        <div className="rounded-lg border bg-card p-6 space-y-3" data-testid="profile-visibility-card">
          <h3 className="text-lg font-semibold mb-1">Visibility & booking</h3>
          {PROFILE_SWITCHES.map(([k, label, help]) => (
            <div key={k} className="flex items-center justify-between gap-4 py-1.5 border-b last:border-0">
              <div><p className="text-sm font-medium">{label}</p><p className="text-xs text-muted-foreground">{help}</p></div>
              <Switch data-testid={`profile-switch-${k}`} checked={!!cur[k]} onCheckedChange={(v) => set(k, v)} />
            </div>
          ))}
          <div className="space-y-1.5 pt-2">
            <Label className="text-xs">Years of experience</Label>
            <Input data-testid="profile-years-input" type="number" value={cur.years_experience}
              onChange={(e) => set("years_experience", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Profile photo URL</Label>
            <Input data-testid="profile-photo-input" value={cur.profile_photo_url} placeholder="https://..."
              onChange={(e) => set("profile_photo_url", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Intro video URL</Label>
            <Input data-testid="profile-video-input" value={cur.intro_video_url} placeholder="https://..."
              onChange={(e) => set("intro_video_url", e.target.value)} />
          </div>
        </div>
        <div className="rounded-lg border bg-card p-6 space-y-3" data-testid="profile-content-card">
          <h3 className="text-lg font-semibold mb-1">Public content</h3>
          <div className="space-y-1.5">
            <Label className="text-xs">Short bio (listing card)</Label>
            <Textarea data-testid="profile-shortbio-input" rows={2} value={cur.short_bio}
              onChange={(e) => set("short_bio", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Long bio (profile page)</Label>
            <Textarea data-testid="profile-longbio-input" rows={4} value={cur.long_bio}
              onChange={(e) => set("long_bio", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Qualifications (one per line)</Label>
            <Textarea data-testid="profile-qualifications-input" rows={3} value={cur.qualifications}
              placeholder={"ABRSM Grade 8 Piano\nB.Mus (Hons)"} onChange={(e) => set("qualifications", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Achievements (one per line)</Label>
            <Textarea data-testid="profile-achievements-input" rows={3} value={cur.achievements}
              onChange={(e) => set("achievements", e.target.value)} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- /admin/growth/enrolments
const ENROLMENT_STATUSES = ["pending_admin_review", "confirmed", "rejected", "cancelled"];

export function GrowthEnrolments() {
  const [status, setStatus] = useState("pending_admin_review");
  const [busyId, setBusyId] = useState(null);
  const { data: items, loading, reload } = useList(
    `/admin/growth/enrolments${status ? `?status=${status}` : ""}`, [status]);

  const update = async (id, values, msg) => {
    try {
      await api.put(`/admin/growth/enrolments/${id}`, { values });
      toast.success(msg);
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const convert = async (id) => {
    setBusyId(id);
    try {
      const r = await api.post(`/admin/growth/enrolments/${id}/convert`, {});
      toast.success(r.data.message, {
        description: r.data.account_claim_required
          ? "Family linked. Secure account claim is still required."
          : "Family linked to an existing parent account.",
        duration: 8000,
      });
      reload();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "object" ? d.message : errMsg(e));
    } finally { setBusyId(null); }
  };

  return (
    <div data-testid="growth-enrolments-page">
      <PageHeader title="Online enrolments"
        description="Self-service enrolments from the public website. 'Approve & invoice' creates the parent, student, weekly schedule and an unpaid invoice in one click." />
      <div className="flex flex-wrap gap-2 mb-5">
        {["", ...ENROLMENT_STATUSES].map((s) => (
          <Button key={s || "all"} size="sm" variant={status === s ? "default" : "outline"}
            onClick={() => setStatus(s)} data-testid={`enrolment-filter-${s || "all"}`}>
            {s ? s.replace(/_/g, " ") : "All"}
          </Button>
        ))}
      </div>
      {loading ? <Spinner full /> : !items?.length ? <EmptyState text="No enrolments match this filter." /> : (
        <div className="space-y-3 stagger-in">
          {items.map((e) => (
            <div key={e.id} className="rounded-lg border bg-card p-4" data-testid={`enrolment-${e.id}`}>
              <div className="flex flex-wrap items-center gap-3">
                <span className="font-mono text-xs font-bold text-muted-foreground">{e.id.slice(0, 8).toUpperCase()}</span>
                <div className="flex-1 min-w-[180px]">
                  <p className="font-semibold text-sm">{e.student_name} <span className="text-muted-foreground font-normal">· parent {e.parent_name}</span></p>
                  <p className="text-xs text-muted-foreground">{e.parent_phone} {e.parent_email && `· ${e.parent_email}`}</p>
                </div>
                <div className="text-xs hidden md:block">
                  <p className="font-medium">{e.course_name} · {e.teacher_name}</p>
                  <p className="text-muted-foreground">{e.selected_date} · {e.selected_start_time}–{e.selected_end_time} · {e.studio_name}</p>
                </div>
                <div className="text-right hidden lg:block">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Payable</p>
                  <Money value={e.total_payable} className="font-bold" />
                </div>
                <StatusBadge status={e.enrolment_status} testId={`enrolment-status-${e.id}`} />
              </div>
              {e.enrolment_status === "pending_admin_review" && (
                <div className="flex flex-wrap gap-2 mt-3">
                  <Button size="sm" disabled={busyId === e.id} onClick={() => convert(e.id)} data-testid={`convert-enrolment-${e.id}`}>
                    <Receipt className="h-3.5 w-3.5 mr-1" />{busyId === e.id ? "Converting..." : "Approve & invoice"}
                  </Button>
                  <Button size="sm" variant="secondary" onClick={() => update(e.id, { enrolment_status: "confirmed" }, "Enrolment confirmed (no invoice yet)")} data-testid={`confirm-enrolment-${e.id}`}>
                    <Check className="h-3.5 w-3.5 mr-1" />Confirm only
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => update(e.id, { enrolment_status: "rejected" }, "Enrolment rejected — slot released")} data-testid={`reject-enrolment-${e.id}`}>
                    <X className="h-3.5 w-3.5 mr-1" />Reject
                  </Button>
                </div>
              )}
              {e.enrolment_status === "confirmed" && !e.converted_invoice_id && (
                <div className="flex gap-2 mt-3">
                  <Button size="sm" disabled={busyId === e.id} onClick={() => convert(e.id)} data-testid={`convert-enrolment-${e.id}`}>
                    <Receipt className="h-3.5 w-3.5 mr-1" />{busyId === e.id ? "Converting..." : "Convert to invoice"}
                  </Button>
                </div>
              )}
              {e.converted_invoice_number && (
                <p className="text-xs mt-3 flex items-center gap-1.5 text-[#2D6A4F] font-medium" data-testid={`converted-badge-${e.id}`}>
                  <Receipt className="h-3.5 w-3.5" />
                  Converted — invoice <Link to="/admin/invoices" className="font-mono font-bold underline underline-offset-2">{e.converted_invoice_number}</Link>
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
const DISPLAY_NAME_OPTIONS = [
  { value: "full_name", label: "Full name" },
  { value: "first_name_only", label: "First name only" },
  { value: "anonymous", label: "Anonymous" },
];

export function GrowthTestimonials() {
  const [tab, setTab] = useState("pending");
  const { data: items, loading, reload } = useList(`/admin/growth/testimonials?status=${tab}`, [tab]);

  const update = async (id, values, msg) => {
    try {
      await api.put(`/admin/growth/testimonials/${id}`, { values });
      toast.success(msg);
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="growth-testimonials-page">
      <PageHeader title="Testimonials"
        description="Only approved testimonials with publish permission ever appear publicly. Student names stay hidden unless the parent allows it." />
      <div className="flex gap-2 mb-5">
        {["pending", "approved", "rejected", "hidden"].map((t) => (
          <Button key={t} size="sm" variant={tab === t ? "default" : "outline"}
            onClick={() => setTab(t)} data-testid={`testimonial-tab-${t}`}>
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </Button>
        ))}
      </div>
      {loading ? <Spinner full /> : !items?.length ? <EmptyState text={`No ${tab} testimonials.`} /> : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 stagger-in">
          {items.map((t) => (
            <div key={t.id} className="rounded-lg border bg-card p-5" data-testid={`testimonial-${t.id}`}>
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <p className="font-semibold text-sm flex-1">For {t.teacher_name}</p>
                <StatusBadge status={t.approval_status} testId={`testimonial-status-${t.id}`} />
                {t.rating != null && (
                  <span className="text-xs font-bold text-[#B8860B]">{"★".repeat(Math.round(t.rating))}</span>
                )}
              </div>
              <p className="text-sm">{t.testimonial_text}</p>
              <div className="flex flex-wrap items-center gap-3 mt-4 text-xs">
                <select data-testid={`display-name-select-${t.id}`} className="border rounded-md px-2 py-1.5 bg-card"
                  value={t.display_name_type || "anonymous"}
                  onChange={(e) => update(t.id, { display_name_type: e.target.value }, "Display name updated")}>
                  {DISPLAY_NAME_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <label className="flex items-center gap-1.5">
                  <Switch data-testid={`permission-switch-${t.id}`} checked={!!t.permission_to_publish}
                    onCheckedChange={(v) => update(t.id, { permission_to_publish: v }, v ? "Publish permission granted" : "Publish permission removed")} />
                  Permission to publish
                </label>
                <span className="text-muted-foreground ml-auto">{fmtDate(t.created_at)}</span>
              </div>
              {t.approval_status !== "approved" && (
                <div className="flex gap-2 mt-3">
                  <Button size="sm" onClick={() => update(t.id, { approval_status: "approved" }, "Testimonial approved")} data-testid={`approve-testimonial-${t.id}`}>
                    <Check className="h-3.5 w-3.5 mr-1" />Approve
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => update(t.id, { approval_status: "rejected" }, "Testimonial rejected")} data-testid={`reject-testimonial-${t.id}`}>
                    <X className="h-3.5 w-3.5 mr-1" />Reject
                  </Button>
                </div>
              )}
              {t.approval_status === "approved" && (
                <Button size="sm" variant="outline" className="mt-3" onClick={() => update(t.id, { approval_status: "hidden" }, "Testimonial hidden")} data-testid={`hide-testimonial-${t.id}`}>
                  <EyeOff className="h-3.5 w-3.5 mr-1" />Hide
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- /admin/growth/enquiries
const ENQUIRY_STATUSES = ["new", "contacted", "trial_booked", "enrolled", "closed"];

export function GrowthEnquiries() {
  const { user } = useAuth();
  const [status, setStatus] = useState("");
  const [channel, setChannel] = useState("");
  const qs = [status && `status=${status}`, channel && `channel=${channel}`].filter(Boolean).join("&");
  const { data: items, loading, reload } = useList(`/admin/growth/enquiries${qs ? `?${qs}` : ""}`, [status, channel]);
  const [noteTarget, setNoteTarget] = useState(null);

  const update = async (id, values, msg) => {
    try {
      await api.put(`/admin/growth/enquiries/${id}`, { values });
      toast.success(msg);
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="growth-enquiries-page">
      <PageHeader title="Leads & enquiries"
        description="Every public enquiry with its source channel — move each lead through the pipeline." />
      <div className="flex flex-wrap gap-2 mb-5">
        {["", ...ENQUIRY_STATUSES].map((s) => (
          <Button key={s || "all"} size="sm" variant={status === s ? "default" : "outline"}
            onClick={() => setStatus(s)} data-testid={`enquiry-filter-${s || "all"}`}>
            {s ? s.replace(/_/g, " ") : "All"}
          </Button>
        ))}
        <select data-testid="enquiry-channel-filter" className="border rounded-md px-3 py-1.5 text-sm bg-card ml-auto"
          value={channel} onChange={(e) => setChannel(e.target.value)}>
          <option value="">All channels</option>
          {SOURCE_CHANNELS.map((c) => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
        </select>
      </div>
      {loading ? <Spinner full /> : !items?.length ? <EmptyState text="No enquiries match this filter." /> : (
        <div className="space-y-3 stagger-in">
          {items.map((e) => (
            <div key={e.id} className="rounded-lg border bg-card p-4" data-testid={`enquiry-${e.id}`}>
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-[180px]">
                  <p className="font-semibold text-sm">{e.parent_name} {e.student_name && <span className="text-muted-foreground font-normal">· {e.student_name}</span>}</p>
                  <p className="text-xs text-muted-foreground">{e.phone} {e.email && `· ${e.email}`}</p>
                </div>
                <div className="text-xs hidden md:block">
                  <p className="font-medium">{e.course_interest || "—"}</p>
                  <p className="text-muted-foreground">{[e.preferred_day, e.preferred_time].filter(Boolean).join(" ") || "Any time"}</p>
                </div>
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#E7F0FE] text-[#1D4ED8]">
                  {(e.source_channel || "website").replace(/_/g, " ")}
                </span>
                <StatusBadge status={e.enquiry_status} testId={`enquiry-status-${e.id}`} />
                <span className="text-xs text-muted-foreground hidden lg:block">{fmtDate(e.created_at)}</span>
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-3">
                <select data-testid={`enquiry-status-select-${e.id}`} className="border rounded-md px-2 py-1.5 text-xs bg-card"
                  value={e.enquiry_status}
                  onChange={(ev) => update(e.id, { enquiry_status: ev.target.value }, "Status updated")}>
                  {ENQUIRY_STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                </select>
                <Button size="sm" variant="outline" data-testid={`assign-me-${e.id}`}
                  onClick={() => update(e.id, { assigned_admin_id: user?.id || "me", assigned_admin_name: user?.full_name || user?.email || "Admin" }, "Assigned to you")}>
                  <UserCheck className="h-3.5 w-3.5 mr-1" />{e.assigned_admin_name ? `Assigned: ${e.assigned_admin_name}` : "Assign to me"}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setNoteTarget(e)} data-testid={`note-enquiry-${e.id}`}>
                  <StickyNote className="h-3.5 w-3.5 mr-1" />{e.notes ? "Edit note" : "Add note"}
                </Button>
                {e.notes && <span className="text-xs text-muted-foreground truncate max-w-xs">“{e.notes}”</span>}
              </div>
            </div>
          ))}
        </div>
      )}
      <FormDialog
        open={!!noteTarget}
        onOpenChange={(o) => !o && setNoteTarget(null)}
        title={`Internal note — ${noteTarget?.parent_name || ""}`}
        fields={[{ name: "notes", label: "Note", type: "textarea", required: true }]}
        initial={{ notes: noteTarget?.notes || "", __key: noteTarget?.id }}
        submitLabel="Save note"
        onSubmit={async (v) => {
          await api.put(`/admin/growth/enquiries/${noteTarget.id}`, { values: { notes: v.notes } });
          toast.success("Note saved");
          reload();
        }}
      />
    </div>
  );
}
